import React from 'react';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { translate } from '../../../locales';
import DialogContent from '../components/DialogContent/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '../../Typography/Typography';
import LoadingButton from '../../LoadingButton/LoadingButton';
import { MessageDialogRoot, MessageDialogHeader } from './MessageDialog.style';
import { dialogClasses as classes } from '../dialogClasses';

const MessageDialog = ({
  DialogHeaderProps: MessageDialogHeaderProps,
  DialogContentProps,
  DialogActionsProps,
  children,
  variant,
  handleClose,
  ConfirmButtonProps = {},
  CancelButtonProps = {}
}) => {
  const {
    action: confirmAction,
    text: confirmText = translate('messageDialog.confirm'),
    ...ConfirmButtonPropsRest
  } = ConfirmButtonProps;
  const {
    action: cancelAction,
    text: cancelText = translate('messageDialog.cancel'),
    ...CancelButtonPropsRest
  } = CancelButtonProps;
  return (
    <MessageDialogRoot>
      <MessageDialogHeader
        onClose={handleClose}
        {...MessageDialogHeaderProps}
      />
      <DialogContent {...DialogContentProps}>
        <Typography variant="body1"> {children}</Typography>
      </DialogContent>
      <DialogActions {...DialogActionsProps}>
        <Button
          data-qa-id="cancel-button"
          onClick={cancelAction}
          color="primary"
          className={clsx(variant, classes.cancelButton)}
          variant="text"
          {...CancelButtonPropsRest}>
          {cancelText}
        </Button>
        <LoadingButton
          data-qa-id="confirm-button"
          onClick={confirmAction}
          color="primary"
          className={variant}
          variant="contained"
          aria-label="loading..."
          {...ConfirmButtonPropsRest}>
          {confirmText}
        </LoadingButton>
      </DialogActions>
    </MessageDialogRoot>
  );
};

MessageDialog.propTypes = {
  DialogHeaderProps: PropTypes.shape({
    title: PropTypes.string.isRequired,
    /**
     * props available in Material's [DialogTitle](https://mui.com/material-ui/api/dialog-title/) component
     **/
    DialogTitleProps: PropTypes.object
  }),
  /**
   * props available in Material's [DialogContent](https://mui.com/material-ui/api/dialog-content/) component
   **/
  DialogContentProps: PropTypes.object,
  /**
   * props available in Material's [DialogActions](https://mui.com/material-ui/api/dialog-actions/) component
   **/
  DialogActionsProps: PropTypes.object,
  children: PropTypes.node,
  handleClose: PropTypes.func,
  variant: PropTypes.oneOf(['message', 'alert']),
  /**
   * In addition to these props this component also accepts all props available in LoadingButton component passing them using ...rest
   **/
  ConfirmButtonProps: PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.func
  }),
  CancelButtonProps: PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.func
  })
};

export default MessageDialog;
